import type { AxiosInstance } from 'axios';
import { unwrap } from '../core/envelope';

/**
 * Live POST /api/Image/upload contract (probed 2026-05-03,
 * `?context=` routing verified 2026-05-05):
 *
 *   Request : multipart/form-data with field name `file` (lowercase)
 *             optional `?context=banner|product|company|user|employee|brand|misc`
 *   Response: ObjectResponse<{ url, fileName, sizeBytes, contentType }>
 *             where `url` is a TENANT-RELATIVE path. The path segment
 *             after the tenant-id matches `context` (defaults to
 *             `misc`):
 *               /uploads/{tenantId}/banner/2026/05/abc123.png
 *               /uploads/{tenantId}/misc/2026/05/abc123.png
 */
export type ImageUploadContext =
  | 'banner'
  | 'product'
  | 'company'
  | 'user'
  | 'employee'
  | 'brand'
  | 'misc';

export interface ImageUploadResult {
  /** Tenant-relative URL — prepend the API host to render. */
  url: string;
  fileName: string;
  sizeBytes: number;
  contentType: string;
}

/**
 * Wraps the multipart upload endpoint so consumers don't have to
 * remember the field name or rebuild the FormData each time. Pass
 * any `Blob` / `File` and you get the persisted URL back. Optional
 * `context` routes the upload to a per-context blob folder so each
 * surface (Banner / Product / etc.) keeps its own slice of storage.
 */
export interface ImageUploadService {
  upload: (
    file: Blob,
    fileName?: string,
    context?: ImageUploadContext,
  ) => Promise<ImageUploadResult>;
}

export function createImageUploadService(client: AxiosInstance): ImageUploadService {
  return {
    upload: async (file, fileName, context) => {
      const fd = new FormData();
      // Field name must be `file` — `File`, `image` etc. are rejected.
      fd.append('file', file, fileName ?? 'upload.bin');
      const url = context ? `/Image/upload?context=${encodeURIComponent(context)}` : '/Image/upload';
      const resp = await client.post(url, fd, {
        // Let the browser set the boundary; do NOT set Content-Type
        // ourselves — Axios will use the right multipart header from
        // FormData automatically.
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return unwrap<ImageUploadResult>(resp.data);
    },
  };
}
